/**
 * Where a delivery's code comes from, and the project's connected repositories (vfs-service src/git/connectedRepo.js,
 * docs/git-source.md). Every service of a project is its own git repo in Flow; Publish › Mia-Platform Console tells Console
 * Next where to fetch each one:
 * - `GET /git-source` -> `{ services: { <name>: { gitUrl, reference, commit, external? } } }`, no token;
 * - one short-lived (1 day) READ token of the person (`POST <vfsServiceUrl>/api/v1/me/git-token`, never a write token);
 * - a connected repository (`/git-remote`: PUT, GET, DELETE, `/test`, `/sync`) mirrors the code to the person's own repository.
 *   The token for it is stored as one of the person's accounts (Features › Connections, OpenBao), the same way the Connections
 *   page does; the project keeps only the account's id.
 * The choices of the Publish journey are remembered in `.mia/project.json` › `publish`.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { createGitToken } from './GitService';
import { addTokenAccount } from './AccountsService';
import { readProjectMetadata, updateProjectMetadata } from '../utils/vfsClient';

export class SourceError extends Error {
  constructor(message, status = 0, code = null, connectUrl = null) {
    super(message);
    this.name = 'SourceError';
    this.status = status;
    this.code = code;
    this.connectUrl = connectUrl;
  }
}

const DAY_MS = 24 * 3600 * 1000;
const base = () => String(config.vfsServiceUrl || '').replace(/\/+$/, '');
const projectUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}${path}`;

async function call(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new SourceError('vfs-service is not configured.', 0, 'not_configured'); }
  let response;
  try {
    response = await getAuthorizedFetch()(url, {
      method,
      headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (error) {
    throw new SourceError(`Your sandbox could not be reached (${error.message}).`, 0, 'unreachable');
  }
  const data = response.status === 204 ? null : await response.json().catch(() => null);
  if (!response.ok) {
    if (response.status === 404 && !data) { throw new SourceError('Your sandbox runs an older version: restart it to connect a repository.', 404, 'sandbox_outdated'); }
    throw new SourceError(data?.error || data?.message || `Request failed (${response.status})`, response.status, data?.code || null, data?.connectUrl || null);
  }
  return data || {};
}

// ---- connected repositories ----

/** `[{ service, url, provider, credentialRef, branch, lastSync }]` (never a token); empty on an older sandbox. */
export async function listConnections(projectId) {
  try {
    const data = await call(projectUrl(projectId, '/git-remote'));
    return Array.isArray(data.remotes) ? data.remotes : [];
  } catch (error) {
    if (error.code === 'sandbox_outdated') { return []; }
    throw error;
  }
}

export const connectRepository = (projectId, { service, url, credentialRef, branch }) => call(projectUrl(projectId, '/git-remote'), {
  method: 'PUT',
  body: { service, url, ...(credentialRef ? { credentialRef } : {}), ...(branch ? { branch } : {}) },
});

export const disconnectRepository = (projectId, service) => call(projectUrl(projectId, `/git-remote?service=${encodeURIComponent(service)}`), { method: 'DELETE' });

/** `git ls-remote` through the sandbox: `{ ok, provider, defaultBranch, branches }`. */
export const testRepository = (projectId, { url, credentialRef }) => call(projectUrl(projectId, '/git-remote/test'), {
  method: 'POST', body: { url, ...(credentialRef ? { credentialRef } : {}) },
});

/** Sends the service's current commit to its repository (never forced): `{ pushed, commit, branch, remote }`. */
export const syncRepository = (projectId, service) => call(projectUrl(projectId, '/git-remote/sync'), { method: 'POST', body: { service } });

/** The account types a repository host can be connected as (the keys of accountTypes.js), and the guess from a URL. */
export const PROVIDER_TYPES = [
  { key: 'github', label: 'GitHub' },
  { key: 'gitlab', label: 'GitLab' },
  { key: 'azure-devops', label: 'Azure DevOps' },
  { key: 'bitbucket', label: 'Bitbucket Cloud' },
  { key: 'bitbucket-dc', label: 'Bitbucket Data Center' },
  { key: 'gitea', label: 'Gitea / Forgejo' },
];

export function providerTypeOf(url) {
  let host = '';
  try { host = new URL(String(url).trim()).host.toLowerCase(); } catch { return null; }
  if (host === 'github.com' || host.startsWith('github.')) { return 'github'; }
  if (host.includes('gitlab')) { return 'gitlab'; }
  if (host === 'dev.azure.com' || host.endsWith('.visualstudio.com') || host.includes('azure')) { return 'azure-devops'; }
  if (host === 'bitbucket.org') { return 'bitbucket'; }
  if (host.includes('bitbucket')) { return 'bitbucket-dc'; }
  return 'gitea';
}

/** The origin of an https URL (`https://host`), or null. */
export function originOf(url) {
  try {
    const parsed = new URL(String(url).trim());
    return parsed.protocol === 'https:' ? parsed.origin : null;
  } catch { return null; }
}

/**
 * Stores a token as one of the person's accounts (the Connections path: validated with the provider, kept in OpenBao) and
 * answers its id, to be used as the connection's `credentialRef`. The token is sent once and not kept here.
 */
export async function saveToken({ type, url, token, username }) {
  const origin = originOf(url);
  if (!origin) { throw new SourceError('The repository must be an https:// address.', 400, 'bad_url'); }
  const host = new URL(origin).host;
  const answer = await addTokenAccount({ type, baseUrl: origin, token, name: `Flow publish · ${host}`, ...(username ? { username } : {}) });
  const id = answer?.account?.id;
  if (!id) { throw new SourceError('Flow could not save the token.', 0, 'no_account_id'); }
  return { id, label: answer.account.label || host, test: answer.test || null };
}

// ---- the source of a delivery ----

/**
 * What `sendToConsole` adds to the delivery's metadata: `source: { <service>: { gitUrl, reference, commit, readToken,
 * expiresAt, external? } }`. Connected repositories are brought up to date first (never forced); one that cannot be gives a
 * `warnings` entry and the delivery goes without `external` for it, with Flow's own source. Best effort as a whole: when
 * Flow cannot say where the code is (an older sandbox, no Git in the plan) the answer is `{ source: null }` and the delivery
 * is the zip as before. The read token is for this one delivery: it is never kept, logged or shown.
 */
export async function deliverySource(projectId, { now = Date.now() } = {}) {
  const warnings = [];
  const connections = await listConnections(projectId).catch(() => []);
  for (const connection of connections) {
    try {
      // eslint-disable-next-line no-await-in-loop -- one repository at a time, like the server's lock
      await syncRepository(projectId, connection.service);
    } catch (error) {
      warnings.push({ service: connection.service, code: error.code || null, message: error.message });
    }
  }
  let services;
  let token;
  try {
    ({ services } = await call(projectUrl(projectId, '/git-source')));
    if (!services || !Object.keys(services).length) { return { source: null, warnings }; }
    token = await createGitToken({ projectId, mode: 'read', ttlDays: 1 });
  } catch {
    return { source: null, warnings };
  }
  if (!token?.token) { return { source: null, warnings }; }
  const expiresAt = token.expiresAt || new Date(now + DAY_MS).toISOString();
  const source = {};
  for (const [name, entry] of Object.entries(services)) {
    if (!entry.gitUrl) { continue; }
    source[name] = {
      gitUrl: entry.gitUrl,
      reference: entry.reference,
      commit: entry.commit,
      readToken: token.token,
      expiresAt,
      ...(entry.external ? { external: entry.external } : {}),
    };
  }
  return { source: Object.keys(source).length ? source : null, warnings };
}

// ---- the journey's choices, per project ----

export const DEFAULT_CHOICE = { code: 'flow', build: 'console-next', ciProvider: null };

/** The remembered choices (`.mia/project.json` › `publish`), defaults for what is missing. */
export async function getPublishChoice(projectId) {
  const metadata = await readProjectMetadata(projectId).catch(() => null);
  const saved = metadata?.publish && typeof metadata.publish === 'object' ? metadata.publish : {};
  return {
    code: saved.code === 'repository' ? 'repository' : 'flow',
    build: saved.build === 'ci' ? 'ci' : 'console-next',
    ciProvider: typeof saved.ciProvider === 'string' ? saved.ciProvider : null,
  };
}

export async function savePublishChoice(projectId, choice) {
  await updateProjectMetadata(projectId, { publish: { code: choice.code, build: choice.build, ciProvider: choice.build === 'ci' ? choice.ciProvider || null : null } });
}
